import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useDebouncer } from '@tanstack/react-pacer'
import {
  Plane,
  CalendarRange,
  ArrowRight,
  Plus,
  CircleCheck,
  Route,
  Search,
} from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { useTripGetAll } from '../lib/hooks'
import { useIsAdmin } from '../hooks/useIsCompanyAdmin'
import { TripDrawer } from './TripDrawer'
import { FlightDrawer } from './FlightDrawer'
import { FirstTripReadyDialog } from './onboarding/FirstTripReadyDialog'
import dayjs from 'dayjs'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from './ui/button'
import { SearchInput } from './ui/search-input'
import { Card } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AppLayout } from './AppLayout'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { EmptyState } from './EmptyState'
import { TripStatusBadge } from './TripStatusBadge'
import { FlightList, HighlightMatch, type FlightListItem } from './FlightList'
import { toStatusBadge, type TripState } from '@/lib/tripState'

type TripTab = 'draft' | 'scheduled' | 'complete'

type TripStatusBadge = ReturnType<typeof toStatusBadge>

// Map tab to which statuses it should show
const TAB_STATUS_MAP: Record<TripTab, TripStatusBadge[]> = {
  draft: ['draft'],
  scheduled: ['scheduled'],
  complete: ['complete'],
}

interface AdminTripsListProps {
  initialSearch?: string
  createTrip?: boolean
  quickStart?: boolean
  initialTab?: TripTab
}

export function AdminTripsList({
  initialSearch,
  createTrip,
  quickStart,
  initialTab,
}: AdminTripsListProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? '')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    initialSearch ?? ''
  )
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(!!createTrip)
  const [activeTab, setActiveTab] = useState<TripTab>(initialTab ?? 'scheduled')
  const [quickStartTripId, setQuickStartTripId] = useState<string | null>(null)
  const [showFirstTripDialog, setShowFirstTripDialog] = useState(false)
  const navigate = useNavigate()
  const { isAdmin } = useIsAdmin()
  const isDesktop = useIsDesktop()

  const debouncedSearch = useDebouncer(
    (query: string) => {
      setDebouncedSearchQuery(query)
      navigate({
        to: '/operations/trips',
        search: { search: query || undefined } as any,
        replace: true,
      })
    },
    { wait: 1000 }
  )

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    debouncedSearch.maybeExecute(query)
  }

  const { data: trips = [], isPending: loading } = useTripGetAll({
    searchQuery: debouncedSearchQuery || undefined,
  })

  // Categorize trips by status
  const tripsWithStatus = trips.map((trip) => ({
    ...trip,
    status: toStatusBadge(trip.tripState as TripState),
  }))

  const isSearching = !!debouncedSearchQuery
  const filteredTrips = isSearching
    ? tripsWithStatus
    : tripsWithStatus
        .filter((trip) => TAB_STATUS_MAP[activeTab].includes(trip.status))
        // Completed trips read newest-first; upcoming tabs keep soonest-first
        .sort((a, b) => {
          if (activeTab !== 'complete') return 0
          if (!a.tripStartDate && !b.tripStartDate) return 0
          if (!a.tripStartDate) return 1
          if (!b.tripStartDate) return -1
          return (
            dayjs(b.tripStartDate).valueOf() - dayjs(a.tripStartDate).valueOf()
          )
        })

  const searchControls = (
    <div className="flex flex-col gap-3">
      <SearchInput
        placeholder="Search trips"
        value={searchQuery}
        onChange={(e) => handleSearchChange(e.target.value)}
        onClear={() => handleSearchChange('')}
        className="w-full h-9"
      />

      {/* Tabs - hidden in search mode */}
      {!isSearching && (
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            const tab = v as TripTab
            setActiveTab(tab)
            navigate({
              to: '/operations/trips',
              search: {
                search: debouncedSearchQuery || undefined,
                tab: tab === 'draft' ? undefined : tab,
              } as any,
              replace: true,
            })
          }}
        >
          <TabsList className="w-full">
            <TabsTrigger value="draft" className="flex-1">
              Draft
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="flex-1">
              Scheduled
            </TabsTrigger>
            <TabsTrigger value="complete" className="flex-1">
              Complete
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
    </div>
  )

  const drawers = (
    <>
      {/* Create First Trip Drawer */}
      <TripDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
        onSuccess={(tripId) => {
          setIsAddDrawerOpen(false)
          if (quickStart) {
            setQuickStartTripId(tripId)
          } else {
            navigate({ to: '/operations/trips/$tripId', params: { tripId } })
          }
        }}
      />

      {/* Quick Start: Trip Leg Drawer after trip creation */}
      <FlightDrawer
        isOpen={!!quickStartTripId}
        onClose={() => {
          setQuickStartTripId(null)
          setShowFirstTripDialog(true)
        }}
        tripId={quickStartTripId ?? ''}
      />

      {/* Quick Start: First Trip Ready Dialog */}
      <FirstTripReadyDialog
        isOpen={showFirstTripDialog}
        onClose={() => setShowFirstTripDialog(false)}
        onGoToDashboard={() => {
          setShowFirstTripDialog(false)
          navigate({ to: '/operations' })
        }}
      />
    </>
  )

  const tripList = (
      <div className="flex flex-col gap-3">
        {/* Trip list */}
        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="flex flex-col gap-1 py-2">
                <div className="flex items-center justify-between py-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
                <div className="border-t border-border-L1 flex items-center pb-2 pt-3">
                  <Skeleton className="h-4 w-28" />
                </div>
              </Card>
            ))}
          </div>
        ) : filteredTrips.length === 0 && isSearching ? (
          <EmptyState
            icon={Search}
            title="No Trip Found"
            description="No matching trip name, aircraft registration, or departure/arrival airport found."
          />
        ) : filteredTrips.length === 0 && activeTab === 'draft' ? (
          <EmptyState
            icon={Route}
            title="No draft trips"
            description="Trips organise flights, crew assignments, and passenger information."
            addButtonText="Create Trip"
            onAdd={() => setIsAddDrawerOpen(true)}
          />
        ) : filteredTrips.length === 0 && activeTab === 'scheduled' ? (
          <EmptyState
            icon={Plane}
            title="No scheduled trips"
            description="Published trips will appear here."
          />
        ) : filteredTrips.length === 0 && activeTab === 'complete' ? (
          <EmptyState
            icon={CircleCheck}
            title="No completed trips"
            description="Trips marked as complete will appear here."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {filteredTrips.map((trip) => {
              const startDate = trip.tripStartDate
                ? dayjs(trip.tripStartDate).format('D MMM')
                : 'N/A'
              const endDate = trip.tripEndDate
                ? dayjs(trip.tripEndDate).format('D MMM')
                : 'N/A'

              return (
                <Link
                  key={trip.id}
                  to="/operations/trips/$tripId"
                  params={{ tripId: trip.id }}
                  className="block"
                >
                  <Card interactive className="flex flex-col gap-1 py-2">
                    {/* Card Header */}
                    <div className="flex items-center justify-between py-2">
                      <span className="font-semibold text-base text-content-primary-bold">
                        <HighlightMatch
                          text={trip.tripName}
                          query={debouncedSearchQuery || undefined}
                        />
                      </span>
                      <TripStatusBadge status={trip.status} />
                    </div>

                    {/* Card Content */}
                    <div className="border-t border-border-L1 flex items-center pb-2 pt-3">
                      {/* Date Range */}
                      <div className="flex items-center gap-1 py-0.5">
                        <CalendarRange className="size-5 text-content-brand-bold" />
                        <span className="text-sm text-content-primary-bold">
                          {startDate}
                        </span>
                        <ArrowRight className="size-3 text-border-3" />
                        <span className="text-sm text-content-primary-bold">
                          {endDate}
                        </span>
                      </div>
                    </div>

                    {/* Flights in search mode */}
                    {isSearching && trip.legs.length > 0 && (
                      <div className="border-t border-border-L1 pt-3">
                        <FlightList
                          flights={trip.legs.map(
                            (leg) =>
                              ({
                                id: leg.id,
                                tripId: trip.id,
                                departureAirportIcao:
                                  leg.departureAirportIcao ?? '',
                                arrivalAirportIcao:
                                  leg.arrivalAirportIcao ?? '',
                                departureDateTime: leg.departureDateTime,
                                arrivalDateTime: leg.arrivalDateTime,
                              }) satisfies FlightListItem
                          )}
                          compact
                          hideCrewPax
                          searchQuery={debouncedSearchQuery}
                        />
                      </div>
                    )}
                  </Card>
                </Link>
              )
            })}
          </div>
        )}
      </div>
  )

  if (isDesktop) {
    return (
      <>
        {drawers}
        <DesktopPage>
          <DesktopPageHeader
            title="Trips"
            subtitle="Control rosters and more…"
            breadcrumb={
              <span>
                <Link to="/operations" className="hover:underline">
                  Operations
                </Link>
                <span className="text-content-primary-subtle"> / Trips</span>
              </span>
            }
            action={
              isAdmin ? (
                <Button
                  roundness="round"
                  variant="brand"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    hapticLight()
                    setIsAddDrawerOpen(true)
                  }}
                >
                  Add Trip
                  <Plus className="size-4 text-content-primary-white" />
                </Button>
              ) : undefined
            }
          />
          {searchControls}
          {tripList}
        </DesktopPage>
      </>
    )
  }

  return (
    <AppLayout
      topBar={
        <>
          <TopBarBackIconButton
            title="Trips"
            rightElement={
              isAdmin ? (
                <Button
                  roundness="round"
                  variant="brand"
                  size="sm"
                  className="size-8 p-0"
                  onClick={() => {
                    hapticLight()
                    setIsAddDrawerOpen(true)
                  }}
                >
                  <Plus className="size-4 text-content-primary-white" />
                </Button>
              ) : undefined
            }
            showBackButton={true}
          />
          <div className="px-gutter flex flex-col gap-3">{searchControls}</div>
        </>
      }
      className="pt-3 scrollbar-hide"
    >
      {tripList}
      {drawers}
    </AppLayout>
  )
}
